import { createFileRoute } from "@tanstack/react-router";
import { PageContent } from "@/components/layout/page-content";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import type { ReceiptVariant } from "@/features/sales/components/bill-printable";
import { useReceiptVariant } from "@/features/sales/hooks/use-receipt-variant";

export const Route = createFileRoute("/(dashboard)/settings/printer")({
	component: PrinterSettingsPage,
});

function PrinterSettingsPage() {
	const [variant, setVariant] = useReceiptVariant();

	return (
		<PageContent title="Printer">
			<div className="space-y-6">
				<div className="flex flex-col gap-2">
					<h2 className="text-2xl font-bold">Printer</h2>
					<p className="text-muted-foreground">
						Choose the receipt format for this device. The setting is stored
						locally — each computer or tablet at this branch can have its own
						printer width.
					</p>
				</div>

				<div className="flex flex-col gap-3 max-w-xl">
					<label
						htmlFor="receipt-variant"
						className="text-sm font-medium text-content-emphasis"
					>
						Receipt format
					</label>
					<Select
						value={variant}
						onValueChange={(next) => setVariant(next as ReceiptVariant)}
					>
						<SelectTrigger
							id="receipt-variant"
							className="w-full justify-between"
						>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="a4">A4 (back-office printer)</SelectItem>
							<SelectItem value="80mm">80mm thermal receipt</SelectItem>
							<SelectItem value="58mm">58mm thermal receipt</SelectItem>
						</SelectContent>
					</Select>
					<p className="text-sm text-muted-foreground">
						<VariantDescription variant={variant} />
					</p>
				</div>
			</div>
		</PageContent>
	);
}

function VariantDescription({ variant }: { variant: ReceiptVariant }) {
	switch (variant) {
		case "a4":
			return (
				<>
					Full tax-invoice layout for laser/inkjet printers. Use when archiving
					copies or printing for accounting.
				</>
			);
		case "80mm":
			return (
				<>
					Most common thermal width. Single-column layout sized for 80mm rolls.
				</>
			);
		case "58mm":
			return (
				<>
					Narrow thermal roll. Tighter typography; use only when the station has
					a 58mm printer.
				</>
			);
	}
}
